import React, { useCallback } from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { Check } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type {
  Dashboard2DayBookingSummary,
  Dashboard2WeekDay
} from "../types/dashboard2-work-status.types";
import {
  dashboard2WeekDayActionAreaSx,
  dashboard2WeekDayCardBodySx,
  dashboard2WeekDayCardDayLabelSx,
  dashboard2WeekDayCardHeaderSx,
  dashboard2WeekDayCardSx,
  dashboard2WeekDayColleagueRowSx,
  dashboard2WeekDayMetaTextSx,
  dashboard2WeekDayPlannedCheckSx,
  dashboard2WeekDayPrimaryTextSx,
  dashboard2WeekDaySecondaryTextSx,
  dashboard2FinishedStatusContentSx,
  dashboard2StatusDetailWrapSx,
  dashboard2WeekDayStatusRowButtonSx,
  dashboard2WeekDayStatusRowSx,
  dashboard2WeekDayStatusSecondaryTextSx,
  dashboard2WeekDayStatusIconSx,
  dashboard2WeekDayStatusLabelSx,
  dashboard2WeekDayStatusTextColumnSx
} from "../dashboard2-status.styles";
import {
  deriveDayBookingPills,
  isWeekDayActionVisible,
  isWeekDayCompactCard,
  isWeekDayPlanned,
  isWeekDayStatusRowClickable,
  selectWeekDayColleague,
  weekDayCardActionKind,
  weekDayColleagueAriaLabel,
  weekDayColleaguePresenceLabel,
  weekDayStatusLines,
  weekDayStatusRowOpensBookingDetail,
  type WeekDayCardActionKind
} from "../utils/dashboard2-week-day-card.utils";
import { weekDayStatusAccentColor } from "../utils/dashboard2-status-display.utils";
import { Dashboard2ColleagueAvatar } from "./Dashboard2ColleagueAvatar";
import { Dashboard2StatusIcon } from "./Dashboard2StatusIcon";
import { Dashboard2WeekDayActionButton } from "./Dashboard2WeekDayActionButton";
import { Dashboard2WeekDayBookingPills } from "./Dashboard2WeekDayBookingPills";
import { Dashboard2WeekDayRowDivider } from "./Dashboard2WeekDayRowDivider";

export const MAX_VISIBLE_PLANNING_COLLEAGUES = 2;

type Dashboard2WeekStatusCellProps = {
  day: Dashboard2WeekDay;
  onUserStatusClick: (day: Dashboard2WeekDay) => void;
  onPrimaryScheduleClick?: (day: Dashboard2WeekDay) => void;
  onWeekDayAction?: (actionKind: WeekDayCardActionKind, day: Dashboard2WeekDay) => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  /** When set, row 1 exposes `aria-expanded` for the open status sheet. */
  openStatusSheetDayKey?: string;
};

const WeekDayStatusIcon: React.FC<{
  status: Dashboard2WeekDay["userStatus"];
  schedule?: BookingScheduleInterface;
  accentColor: string;
}> = ({ status, schedule, accentColor }) => {
  const theme = useTheme();

  return (
    <Box sx={dashboard2WeekDayStatusIconSx(theme, accentColor)}>
      <Dashboard2StatusIcon status={status} schedule={schedule} iconSize={16} />
    </Box>
  );
};

const WeekDayStatusRowContent: React.FC<{
  dayKey: string;
  userStatus: Dashboard2WeekDay["userStatus"];
  primarySchedule?: BookingScheduleInterface;
  accent: string;
  statusLines: ReturnType<typeof weekDayStatusLines>;
}> = ({ dayKey, userStatus, primarySchedule, accent, statusLines }) => {
  const theme = useTheme();
  const secondaryDetailSx = {
    ...(dashboard2WeekDaySecondaryTextSx as object),
    ...(dashboard2StatusDetailWrapSx as object),
    ...(dashboard2WeekDayStatusSecondaryTextSx(theme, userStatus.kind) as object)
  };

  return (
    <>
      <WeekDayStatusIcon status={userStatus} schedule={primarySchedule} accentColor={accent} />
      <Box
        sx={{
          ...(dashboard2WeekDayStatusTextColumnSx as object),
          ...(userStatus.isFinished ? (dashboard2FinishedStatusContentSx(theme) as object) : {})
        }}
      >
        <Typography
          component="div"
          sx={{
            ...(dashboard2WeekDayPrimaryTextSx as object),
            ...(dashboard2StatusDetailWrapSx as object),
            ...(dashboard2WeekDayStatusLabelSx(theme, userStatus.kind) as object)
          }}
          data-testid={`dashboard2-user-status-${dayKey}`}
        >
          {statusLines.primary}
        </Typography>
        {statusLines.inventory && (
          <Typography
            component="div"
            sx={secondaryDetailSx}
            data-testid={`dashboard2-user-status-detail-${dayKey}`}
          >
            {statusLines.inventory}
          </Typography>
        )}
        {statusLines.time && (
          <Typography
            component="div"
            sx={secondaryDetailSx}
            data-testid={`dashboard2-user-status-time-${dayKey}`}
          >
            {statusLines.time}
          </Typography>
        )}
        {statusLines.secondary && (
          <Typography
            component="div"
            sx={secondaryDetailSx}
            data-testid={`dashboard2-user-status-detail-${dayKey}`}
          >
            {statusLines.secondary}
          </Typography>
        )}
      </Box>
    </>
  );
};

export const Dashboard2WeekStatusCell: React.FC<Dashboard2WeekStatusCellProps> = ({
  day,
  onUserStatusClick,
  onPrimaryScheduleClick,
  onWeekDayAction,
  onBookingClick,
  openStatusSheetDayKey
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userStatus } = day;
  const isUnplanned = userStatus.kind === "unplanned";
  const isHoliday = userStatus.kind === "public_holiday";
  const isCompactCard = isWeekDayCompactCard(userStatus);
  const isPlanned = isWeekDayPlanned(userStatus);
  const colleague = selectWeekDayColleague(userStatus, day.colleagues);
  const actionKind = weekDayCardActionKind(userStatus);
  const statusLines = weekDayStatusLines(userStatus, t);
  const accent = weekDayStatusAccentColor(theme, userStatus.kind);
  const bookingPills = deriveDayBookingPills(userStatus);
  const showBookingsRow = bookingPills.length > 0;

  const handleAction = useCallback(
    (kind: WeekDayCardActionKind) => {
      if (onWeekDayAction) {
        onWeekDayAction(kind, day);
        return;
      }

      if (kind === "plan_day" || kind === "plan_holiday") {
        onUserStatusClick(day);
      }
    },
    [day, onUserStatusClick, onWeekDayAction]
  );

  const statusAriaSummary = [
    statusLines.primary,
    statusLines.inventory,
    statusLines.time,
    statusLines.secondary
  ]
    .filter(Boolean)
    .join(", ");
  const opensBookingDetail = weekDayStatusRowOpensBookingDetail(day);
  const isStatusClickable = isWeekDayStatusRowClickable(day);
  const statusButtonAriaLabel = opensBookingDetail
    ? t("_dashboard2_viewBookingDetail")
    : t("_dashboard2_todayWorkStatusTriggerAria", {
        status: statusAriaSummary
      });
  const isStatusSheetOpen = openStatusSheetDayKey === day.dayKey;

  const handleStatusClick = useCallback(() => {
    if (!isStatusClickable) {
      return;
    }

    if (opensBookingDetail) {
      onPrimaryScheduleClick?.(day);
      return;
    }

    onUserStatusClick(day);
  }, [day, isStatusClickable, onPrimaryScheduleClick, onUserStatusClick, opensBookingDetail]);

  const statusRowContent = (
    <WeekDayStatusRowContent
      dayKey={day.dayKey}
      userStatus={userStatus}
      primarySchedule={day.primarySchedule}
      accent={accent}
      statusLines={statusLines}
    />
  );

  const actionLabel = (() => {
    if (actionKind === "floor_plan") {
      return t("_dashboard2_floorPlan");
    }
    if (actionKind === "book_place") {
      return t("_dashboard2_bookAPlace");
    }
    if (actionKind === "plan_day" || actionKind === "plan_holiday") {
      return t("_dashboard2_weekPlanningDayAction");
    }
    return undefined;
  })();

  return (
    <Box
      sx={dashboard2WeekDayCardSx(theme, {
        isToday: day.isToday,
        isUnplanned,
        isHoliday
      })}
      data-testid={`dashboard2-planning-day-${day.dayKey}`}
    >
      <Box sx={dashboard2WeekDayCardHeaderSx}>
        <Typography component="div" sx={dashboard2WeekDayCardDayLabelSx(theme, day.isToday)}>
          {day.label}
        </Typography>
        {isPlanned && (
          <Check
            sx={dashboard2WeekDayPlannedCheckSx(theme)}
            aria-label={t("_dashboard2_dayPlannedAria")}
            data-testid="dashboard2-week-day-planned-check"
          />
        )}
      </Box>

      <Box sx={dashboard2WeekDayCardBodySx}>
        {isHoliday || !isStatusClickable ? (
          <Box sx={dashboard2WeekDayStatusRowSx} data-testid="dashboard2-planning-day-user">
            {statusRowContent}
          </Box>
        ) : (
          <ButtonBase
            type="button"
            sx={dashboard2WeekDayStatusRowButtonSx(theme)}
            onClick={handleStatusClick}
            aria-label={statusButtonAriaLabel}
            aria-haspopup={opensBookingDetail ? undefined : "dialog"}
            aria-expanded={opensBookingDetail ? undefined : isStatusSheetOpen}
            data-testid="dashboard2-planning-day-user"
          >
            {statusRowContent}
          </ButtonBase>
        )}

        {showBookingsRow && (
          <>
            <Dashboard2WeekDayRowDivider />
            <Dashboard2WeekDayBookingPills
              dayLabel={day.label}
              status={userStatus}
              onBookingClick={onBookingClick}
            />
          </>
        )}

        {colleague && (
          <>
            <Dashboard2WeekDayRowDivider />
            <Box
              sx={dashboard2WeekDayColleagueRowSx}
              data-testid="dashboard2-planning-day-colleagues"
            >
              <Dashboard2ColleagueAvatar
                colleague={{
                  userId: colleague.colleagueId,
                  firstName: colleague.firstName,
                  surname: colleague.surname,
                  photoUrl: colleague.photoUrl
                }}
              />
              <Typography
                component="div"
                color="text.secondary"
                sx={{
                  ...dashboard2WeekDayMetaTextSx,
                  ...(dashboard2StatusDetailWrapSx as object),
                  minWidth: 0,
                  flex: 1
                }}
                aria-label={weekDayColleagueAriaLabel(colleague, userStatus, t)}
              >
                {weekDayColleaguePresenceLabel(colleague, userStatus, t)}
              </Typography>
            </Box>
          </>
        )}

        {!isCompactCard && <Box sx={{ flex: 1, minHeight: 0 }} aria-hidden />}

        {actionKind && isWeekDayActionVisible(day) && actionLabel && (
          <Box sx={dashboard2WeekDayActionAreaSx}>
            <Dashboard2WeekDayRowDivider />
            <Dashboard2WeekDayActionButton
              actionKind={actionKind}
              label={actionLabel}
              onClick={() => handleAction(actionKind)}
              data-testid={`dashboard2-week-day-action-${day.dayKey}`}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
